import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Clock,
  Search,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  MoreHorizontal,
} from 'lucide-react';
import { httpMethodLabelClass } from '../utils/httpMethodLabelClass';
import { usePreferencesStore } from '../store/preferencesStore';
import OsScroll from './ui/OsScroll';

function formatRequestTime(timestamp, timeFormat) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(
    i18n.language === 'de' ? 'de-DE' : 'en-US',
    { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' }
  );
}

/** Day-bucket label: "Today" / "Yesterday" / "Month Day" — matches the grouped history layout. */
function getDayBucket(timestamp) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return { key: 'unknown', label: i18n.t('common.unknown'), sortValue: -Infinity };
  }

  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const diffDays = Math.round((startOfToday - startOfDay) / (24 * 60 * 60 * 1000));
  const key = startOfDay.toISOString().slice(0, 10);
  const sortValue = startOfDay.getTime();

  if (diffDays === 0) {
    return { key, label: i18n.t('sidebar.today'), sortValue };
  }
  if (diffDays === 1) {
    return { key, label: i18n.t('sidebar.yesterday'), sortValue };
  }
  const sameYear = startOfDay.getFullYear() === startOfToday.getFullYear();
  const label = date.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return { key, label, sortValue };
}

const History = ({ history, onSelectFromHistory, onClearHistory }) => {
  const { t } = useTranslation();
  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHistoryKey, setSelectedHistoryKey] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredHistory = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) {
      return history;
    }
    return history.filter(
      (item) =>
        (item.url || '').toLowerCase().includes(q) ||
        (item.method || '').toLowerCase().includes(q)
    );
  }, [history, searchTerm]);

  /** Group by day-bucket, preserving incoming order within each group (incoming history is newest-first). */
  const groups = useMemo(() => {
    const byKey = new Map();
    for (let i = 0; i < filteredHistory.length; i++) {
      const item = filteredHistory[i];
      const bucket = getDayBucket(item.timestamp);
      let group = byKey.get(bucket.key);
      if (!group) {
        group = { key: bucket.key, label: bucket.label, sortValue: bucket.sortValue, items: [] };
        byKey.set(bucket.key, group);
      }
      group.items.push({ item, originalIndex: i });
    }
    return Array.from(byKey.values()).sort((a, b) => b.sortValue - a.sortValue);
  }, [filteredHistory]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return (
    <div className="flex h-full w-full flex-col bg-gray-50 shadow-sm dark:bg-zinc-900">
      <div className="flex-shrink-0 border-b border-gray-200 px-2 pt-2 dark:border-zinc-800">
        <div className="sidebar-panel-search-row">
          <Search size={14} className="flex-shrink-0 text-gray-400 dark:text-zinc-500" aria-hidden />
          <input
            type="text"
            placeholder={t('sidebar.searchHistory')}
            value={searchTerm}
            onChange={handleSearchChange}
            className="tab-dropdown-search-input min-w-0 flex-1 border-none bg-transparent p-0 text-gray-900 placeholder-gray-400 focus:outline-none dark:text-zinc-100 dark:placeholder-zinc-500"
            autoComplete="off"
          />
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
            {searchTerm ? (
              <button
                type="button"
                onClick={clearSearch}
                className="flex h-full w-full cursor-pointer items-center justify-center rounded bg-transparent p-0 text-gray-900 hover:opacity-70 focus:outline-none dark:text-zinc-300"
                aria-label={t('sidebar.clearSearch')}
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <OsScroll className="flex-1 min-h-0 px-2 pb-3">
        <div className="pt-1">
          <div className="flex items-center justify-between pb-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-400">
              {t('sidebar.requestHistory')}
            </h2>
            {history.length > 0 && (
              <button
                onClick={onClearHistory}
                type="button"
                className="rounded bg-transparent p-1 text-gray-500 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:text-zinc-400 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                title={t('sidebar.clearHistory')}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
        {filteredHistory.length === 0 ? (
          <div className="py-8 text-center text-gray-500 dark:text-zinc-400">
            <div className="sidebar-empty-icon-wrap">
              <div className="sidebar-empty-icon">
                <Clock size={16} />
              </div>
            </div>
            <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-200">
              {history.length === 0 ? t('sidebar.noRequestsInHistory') : t('sidebar.noHistorySearch')}
            </p>
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              {history.length === 0
                ? t('sidebar.historyAppearHint')
                : t('sidebar.noHistorySearchHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {groups.map((group) => {
              const collapsed = collapsedGroups.has(group.key);
              return (
                <div key={group.key} className="animate-fadeIn">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="group flex w-full cursor-pointer items-center gap-1 rounded-md bg-transparent px-2 py-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-zinc-800/80"
                    title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
                  >
                    <span className="flex shrink-0 items-center">
                      {collapsed ? (
                        <ChevronRight size={14} className="text-gray-600 dark:text-zinc-400" />
                      ) : (
                        <ChevronDown size={14} className="text-gray-600 dark:text-zinc-400" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-gray-700 dark:text-zinc-300">
                      {group.label}
                    </span>
                    <span className="shrink-0 text-xxs tabular-nums text-gray-400 dark:text-zinc-500">
                      {group.items.length}
                    </span>
                  </button>

                  {!collapsed && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200/80 pl-1 dark:border-zinc-700/80">
                      {group.items.map(({ item, originalIndex }) => {
                        const rowKey = `${item.timestamp}-${originalIndex}`;
                        const isSelected = selectedHistoryKey === rowKey;
                        return (
                          <div
                            key={rowKey}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedHistoryKey(rowKey);
                              onSelectFromHistory(item);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedHistoryKey(rowKey);
                                onSelectFromHistory(item);
                              }
                            }}
                            className={`group flex cursor-pointer items-center rounded-md border px-2 py-1 transition-all duration-200 ${
                              isSelected
                                ? 'border-blue-200 bg-blue-50 dark:border-blue-800/80 dark:bg-blue-950/50'
                                : 'border border-transparent hover:border-gray-200 hover:bg-gray-100 dark:hover:border-zinc-700 dark:hover:bg-zinc-800/80'
                            }`}
                            title={item.url || ''}
                          >
                            <span
                              className={`sidebar-request-method shrink-0 font-semibold uppercase tracking-wide mr-2 pointer-events-none ${httpMethodLabelClass(item.method)}`}
                            >
                              {item.method}
                            </span>
                            <span className="pointer-events-none min-w-0 flex-1 truncate text-xs text-gray-900 dark:text-zinc-100">
                              {item.url || t('sidebar.noUrl')}
                            </span>
                            {item.timestamp && (
                              <span className="pointer-events-none shrink-0 text-xxs tabular-nums text-gray-400 dark:text-zinc-500 ml-1">
                                {formatRequestTime(item.timestamp, timeFormat)}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedHistoryKey(rowKey);
                                onSelectFromHistory(item);
                              }}
                              className="shrink-0 cursor-pointer rounded bg-transparent p-1 opacity-0 transition-colors duration-150 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-zinc-700"
                              title={t('sidebar.openRequest')}
                            >
                              <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </OsScroll>
    </div>
  );
};

export default History;
